import React, { useState, useMemo, useEffect, useContext } from "react";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import {
  notification,
  Checkbox,
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
} from "antd";
import { webAPIs } from "../config/webAPIs";
import axios from "axios";
import axiosInstance from "../config/axiosInstance";
import "react-phone-number-input/style.css";
import PhoneInput, { isPossiblePhoneNumber, getCountryCallingCode } from "react-phone-number-input";
import countryList from "react-select-country-list";
import { getUtmParams } from '../utils/utmHelper';
import banner1 from "../assets/banner/banner-1.png";
import banner2 from "../assets/banner/banner-2.png";
import banner3 from "../assets/banner/banner-3.png";
import banner4 from "../assets/banner/banner-4.png";
import banner5 from "../assets/banner/banner-5.jpg";
import { AuthContext } from "../AuthContext";

const { Option } = Select;

const Register = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const options = useMemo(() => countryList().getData(), []);
  const [selectedCountry, setSelectedCountry] = useState("CH");
  const [phoneValue, setPhoneValue] = useState("");
  const navigate = useNavigate();
  const { loggedIn } = useContext(AuthContext);

  useEffect(() => {
    if (loggedIn) {
      navigate("/dashboard");
    }
  }, [loggedIn, navigate]);

  useEffect(() => {
    form.setFieldsValue({
      country: "CH",
      agree: false,
    });
    setSelectedCountry("CH");
    setPhoneValue("");
  }, [form]);

  const validatePhoneNumber = (rule, value) => {
    if (!value || isPossiblePhoneNumber(value)) {
      return Promise.resolve();
    } else {
      return Promise.reject(new Error("Please enter a valid phone number!"));
    }
  };

  const noWhiteSpace  = (_, value) => {
    if (!value || value.trim().length === 0) {
      return Promise.reject(new Error('This field cannot be blank!'));
    }
    return Promise.resolve();
  };

  const handleFormSubmit = async (values) => {
    if (values.password !== values.confirmPassword) {
      notification.error({
        message: "Password Error",
        description: "Passwords do not match!",
        duration: 5,
      });
      return;
    }

    try {
      setIsLoading(true);
      const utmParams = getUtmParams();
      const { data } = await axiosInstance.post(webAPIs.signUp, {
        ...values,
        utmSource: utmParams.utmSource,
        utmMedium: utmParams.utmMedium,
        utmCampaign: utmParams.utmCampaign
      });
      setIsLoading(false);
      if (data.success) {
        // Track successful signup with LinkedIn
        if (window.lintrk) {
          window.lintrk('track', { conversion_id: 20966682 });
        }

        form.resetFields();
        notification.success({
          message: "Success",
          description: data.message,
          duration: 5,
        });
        // Redirect to login page for email verification flow
        navigate("/sign-in");
      } else {
        notification.error({
          message: "Registration Error",
          description: data.message,
          duration: 5,
        });
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      notification.error({
        message: "",
        description: error.response?.data?.message || error.message || "An error occurred",
        duration: 5,
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-intro">
      <div className="logo-area w-full max-w-md">
        <img
          src={`${process.env.PUBLIC_URL}/insightlab_gold.png`}
          alt="logo"
          className="hidden sm:block"
        />
        <img
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="logo"
          className="block sm:hidden p-2"
        />
      </div>
      <div className="registration-container">
        <div className="registration-text">
          <h1>Insight-lab.ai creates your consumer insight reports</h1>
          <p>Free your time for strategy and creativity.</p>
          <p>Our AI-powered platform turns interviews, focus groups, and feedback into clear reports with quotes and recommendations. Your end-to-end research assistant, Insight-lab streamlines the entire process—faster, more efficient, and consistently high-quality. Fully GDPR-compliant, your data stays safe and confidential.</p>
        </div>
        <div className="registration-tiles">
          <div className="registration-tile">
            <svg aria-hidden="true" className="registration-tile-icon" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg"><path d="M288 248v28c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-28c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12zm-12 72H108c-6.6 0-12 5.4-12 12v28c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12v-28c0-6.6-5.4-12-12-12zm108-188.1V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48C0 21.5 21.5 0 48 0h204.1C264.8 0 277 5.1 286 14.1L369.9 98c9 8.9 14.1 21.2 14.1 33.9zm-128-80V128h76.1L256 51.9zM336 464V176H232c-13.3 0-24-10.7-24-24V48H48v416h288z"></path></svg>
            <h2>Upload Transcripts</h2>
            <p>Easily import your interviews, feedback, or focus group data. Insight-lab accepts Word, PDF, and Excel. Audio files (mp3/M4A) are auto-transcribed and ready for analysis.</p>
          </div>
          <div className="registration-tile">
            <svg aria-hidden="true" className="registration-tile-icon" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M432,320H400a16,16,0,0,0-16,16V448H64V128H208a16,16,0,0,0,16-16V80a16,16,0,0,0-16-16H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V336A16,16,0,0,0,432,320ZM488,0h-128c-21.37,0-32.05,25.91-17,41l35.73,35.73L135,320.37a24,24,0,0,0,0,34L157.67,377a24,24,0,0,0,34,0L435.28,133.32,471,169c15,15,41,4.5,41-17V24A24,24,0,0,0,488,0Z"></path></svg>
            <h2>AI-Powered Analysis</h2>
            <p>Our LLM-based engine identifies key themes, patterns, and emotions. Simply choose the type and length of summary you want—Insight-lab does the rest.</p>
          </div>
          <div className="registration-tile">
            <svg aria-hidden="true" className="registration-tile-icon" viewBox="0 0 544 512" xmlns="http://www.w3.org/2000/svg"><path d="M527.79 288H290.5l158.03 158.03c6.04 6.04 15.98 6.53 22.19.68 38.7-36.46 65.32-85.61 73.13-140.86 1.34-9.46-6.51-17.85-16.06-17.85zm-15.83-64.8C503.72 103.74 408.26 8.28 288.8.04 279.68-.59 272 7.1 272 16.24V240h223.77c9.14 0 16.82-7.68 16.19-16.8zM224 288V50.71c0-9.55-8.39-17.4-17.84-16.06C86.99 51.49-4.1 155.6.14 280.37 4.5 408.51 114.83 513.59 243.03 511.98c50.4-.63 96.97-16.87 135.26-44.03 7.9-5.6 8.42-17.23 1.57-24.08L224 288z"></path></svg>
            <h2>Insightful Reports</h2>
            <p>Get structured reports with verbatim quotes and clear narratives. Export to Word, adjust content or layout, and you’re done.</p>
          </div>
          <div className="registration-tile">
            <svg aria-hidden="true" className="registration-tile-icon" viewBox="0 0 352 512" xmlns="http://www.w3.org/2000/svg"><path d="M176 80c-52.94 0-96 43.06-96 96 0 8.84 7.16 16 16 16s16-7.16 16-16c0-35.3 28.72-64 64-64 8.84 0 16-7.16 16-16s-7.16-16-16-16zM96.06 459.17c0 3.15.93 6.22 2.68 8.84l24.51 36.84c2.97 4.46 7.97 7.14 13.32 7.14h78.85c5.36 0 10.36-2.68 13.32-7.14l24.51-36.84c1.74-2.62 2.67-5.7 2.68-8.84l.05-43.18H96.02l.04 43.18zM176 0C73.72 0 0 82.97 0 176c0 44.37 16.45 84.85 43.56 115.78 16.64 18.99 42.74 58.8 52.42 92.16v.06h48v-.12c-.01-4.77-.72-9.51-2.15-14.07-5.59-17.81-22.82-64.77-62.17-109.67-20.54-23.43-31.52-53.15-31.61-84.14-.2-73.64 59.67-128 127.95-128 70.58 0 128 57.42 128 128 0 30.97-11.24 60.85-31.65 84.14-39.11 44.61-56.42 91.47-62.1 109.46a47.507 47.507 0 0 0-2.22 14.3v.1h48v-.05c9.68-33.37 35.78-73.18 52.42-92.16C335.55 260.85 352 220.37 352 176 352 78.8 273.2 0 176 0z"></path></svg>
            <h2>Actionable Recommendations</h2>
            <p>Turn insights into decisions. Insight-lab generates tailored recommendations aligned with your goals. Bridge the gap between research findings and business impact with AI-driven suggestions.</p>
          </div>
          <div className="registration-tile">
            <svg aria-hidden="true" className="registration-tile-icon" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M466.5 83.7l-192-80a48.15 48.15 0 0 0-36.9 0l-192 80C27.7 91.1 16 108.6 16 128c0 198.5 114.5 335.7 221.5 380.3 11.8 4.9 25.1 4.9 36.9 0C360.1 472.6 496 349.3 496 128c0-19.4-11.7-36.9-29.5-44.3zM256.1 446.3l-.1-381 175.9 73.3c-3.3 151.4-82.1 261.1-175.8 307.7z"></path></svg>
            <h2>Data Security</h2>
            <p>Your information stays private and protected. The platform is fully GDPR-compliant. We safeguard your research with enterprise-grade security and confidentiality standards.</p>
          </div>
          <div className="registration-tile">
            <svg aria-hidden="true" className="registration-tile-icon" viewBox="0 0 640 512" xmlns="http://www.w3.org/2000/svg"><path d="M96 224c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64zm448 0c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64zm32 32h-64c-17.6 0-33.5 7.1-45.1 18.6 40.3 22.1 68.9 62 75.1 109.4h66c17.7 0 32-14.3 32-32v-32c0-35.3-28.7-64-64-64zm-256 0c61.9 0 112-50.1 112-112S381.9 32 320 32 208 82.1 208 144s50.1 112 112 112zm76.8 32h-8.3c-20.8 10-43.9 16-68.5 16s-47.6-6-68.5-16h-8.3C179.6 288 128 339.6 128 403.2V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-28.8c0-63.6-51.6-115.2-115.2-115.2zm-223.7-13.4C161.5 263.1 145.6 256 128 256H64c-35.3 0-64 28.7-64 64v32c0 17.7 14.3 32 32 32h65.9c6.3-47.4 34.9-87.3 75.2-109.4z"></path></svg>
            <h2>Collaboration Made Simple</h2>
            <p>Work with your team, share findings, and define next steps. Provide stakeholders with interactive, insight-driven access to your results.</p>
          </div>

        </div>
        <div className="registration-form">
          <div className="bg-login register-form p-8 rounded-lg shadow-lg w-full max-w-xl mx-auto">
            <h2 className="text-2xl font-semibold mb-6 text-gray-900 text-center">
              Create Your Account
            </h2>
            <Form
              form={form}
              onFinish={handleFormSubmit}
              layout="vertical"
              autoComplete="on"
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="First Name"
                    name="firstName"
                    rules={[
                      { required: true, message: "Please input your first name!" },
                      { validator: noWhiteSpace },
                    ]}
                  >
                    <Input autoComplete="given-name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Last Name"
                    name="lastName"
                    rules={[
                      { required: true, message: "Please input your last name!" },
                      { validator: noWhiteSpace },
                    ]}
                  >
                    <Input autoComplete="family-name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="Company Name" 
                    name="companyName" 
                    rules={[
                      {
                        required: true,
                        message: "Please input your Company Name!",
                      },
                      { validator: noWhiteSpace },
                    ]}>
                    <Input autoComplete="organization" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="Street" 
                    name="street" 
                    rules={[]}
                  >
                    <Input autoComplete="street-address" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    name="city"
                    rules={[]}
                  >
                    <Input autoComplete="address-level2" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Postal Code"
                    name="postalCode"
                    rules={[]}
                  >
                    <Input autoComplete="postal-code" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Country"
                    name="country"
                    rules={[
                      { required: true, message: "Please select your country!" },
                      { validator: noWhiteSpace },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      defaultValue="CH"
                      onChange={(value) => {
                        // If phoneValue is empty or only contains the previous country code, update to new country code
                        const prevCode = selectedCountry ? "+" + getCountryCallingCode(selectedCountry) : "";
                        const newCode = value ? "+" + getCountryCallingCode(value) : "";
                        if (!phoneValue || phoneValue === prevCode) {
                          setPhoneValue(newCode);
                          form.setFieldsValue({ phoneNumber: newCode });
                        }
                        setSelectedCountry(value);
                        form.setFieldsValue({ country: value });
                      }}
                    >
                      {options.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Phone Number"
                    name="phoneNumber"
                    rules={[
                      {
                        required: true,
                        message: "Please input your phone number!",
                      },
                      { validator: validatePhoneNumber },
                    ]}
                  >
                    <PhoneInput
                      international
                      defaultCountry={selectedCountry}
                      country={selectedCountry}
                      placeholder="Enter phone number"
                      value={phoneValue}
                      onChange={(value) => {
                        setPhoneValue(value);
                        form.setFieldsValue({ phoneNumber: value });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      {
                        type: "email",
                        required: true,
                        message: "Please input your email!",
                      },
                    ]}
                  >
                    <Input autoComplete="email" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your password",
                      },
                      {
                        min: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      {
                        pattern: /^(?=.*[a-z]).*$/,
                        message: "Password must contain at least one lowercase letter",
                      },
                      {
                        pattern: /^(?=.*[A-Z]).*$/,
                        message: "Password must contain at least one uppercase letter",
                      },
                      {
                        pattern: /^(?=.*\d).*$/,
                        message: "Password must contain at least one number",
                      },
                      {
                        pattern: /^(?=.*[\W_]).*$/,
                        message: "Password must contain at least one special character",
                      },
                    ]}
                    hasFeedback
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Confirm Password"
                    name="confirmPassword"
                    dependencies={["password"]}
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password!",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              "The two passwords that you entered do not match!"
                            )
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                </Col>

                <Form.Item
                  name="agree"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "You must agree with the terms and conditions."
                              )
                            ),
                    },
                  ]}
                >
                  <Checkbox>
                    I agree with the{" "}
                      <Typography.Link
                        href="https://insight-lab.ai/terms-and-conditions/"
                        target="_blank"
                      >
                        terms and conditions
                      </Typography.Link>
                  </Checkbox>
                </Form.Item>
                <Col span={24}>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      loading={isLoading}
                      className="submit-btn"
                    >
                      Register
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <div className="text-center mt-4">
              Already have an account?
              <Link to="/sign-in" className="link-text hover:underline ml-1">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="registration-banner">
        <img src={banner1} alt="Banner 1" className="registration-banner-image" />
        <img src={banner2} alt="Banner 2" className="registration-banner-image" />
        <img src={banner3} alt="Banner 3" className="registration-banner-image" />
        <img src={banner4} alt="Banner 4" className="registration-banner-image" />
        <img src={banner5} alt="Banner 5" className="registration-banner-image" />
      </div>

      <Footer currentPage="Register" />
    </div>
  );
};

export default Register;
